
import SafeMotion from '@/components/motion/SafeMotion';
import MotionErrorBoundary from '@/components/motion/MotionErrorBoundary';
import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { MappedHeroContent } from '@/hooks/useHeroData';
import { AnimatePresence } from 'framer-motion';

interface HeroContentProps {
  content: MappedHeroContent;
  currentIndex: number;
}

const HeroContent = ({ content, currentIndex }: HeroContentProps) => {
  const navigate = useNavigate();

  return (
    <MotionErrorBoundary>
      <SafeMotion
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        className="text-center md:text-left"
      >
        <div className="min-h-[380px] sm:min-h-[420px] md:min-h-[450px] flex flex-col justify-center">
          <AnimatePresence mode="wait">
            <SafeMotion
              key={currentIndex}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
              transition={{ duration: 0.7 }}
              className="flex flex-col"
            >
              <SafeMotion
                className="inline-flex items-center gap-2 bg-muted/50 border border-border rounded-full px-4 py-2 mb-6 backdrop-blur-sm self-center md:self-start"
                whileHover={{ scale: 1.05, y: -2 }}
              >
                <Sparkles className="h-4 w-4 text-luxury-gold" />
                <span className="text-foreground font-semibold text-sm">{content.supertitle}</span>
              </SafeMotion>

              <SafeMotion
                as="h1"
                id="hero-heading"
                className="font-playfair text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight text-foreground"
              >
                {content.titleLine1}
                <br />
                {content.titleLine2} <span className="text-luxury-gold">{content.titleHighlight}</span>.
              </SafeMotion>

              <SafeMotion
                as="p"
                className="font-poppins text-lg sm:text-xl mb-8 text-muted-foreground leading-relaxed max-w-lg mx-auto md:mx-0"
              >
                {content.subtitle}
              </SafeMotion>
            </SafeMotion>
          </AnimatePresence>
        </div>

        <SafeMotion
          className="flex flex-col sm:flex-row gap-4 mb-8 justify-center md:justify-start"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.8 }}
        >
          <SafeMotion whileHover={{ scale: 1.05, y: -3 }} whileTap={{ scale: 0.95 }}>
            <Button
              size="lg"
              onClick={() => navigate(content.cta1.path)}
              className="group w-full sm:w-auto min-h-[48px] relative overflow-hidden rounded-full font-semibold px-8 shadow-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-all"
            >
              {content.cta1.text}
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </SafeMotion>

          {content.cta2.text && content.cta2.path && (
            <SafeMotion whileHover={{ scale: 1.05, y: -3 }} whileTap={{ scale: 0.95 }}>
              <Button
                variant="outline"
                size="lg"
                onClick={() => navigate(content.cta2.path!)}
                className="w-full sm:w-auto min-h-[48px] rounded-full font-semibold px-8 backdrop-blur-md bg-background/30 border-2 border-border hover:bg-muted/80 hover:border-border transition-all"
              >
                {content.cta2.text}
              </Button>
            </SafeMotion>
          )}
        </SafeMotion>
      </SafeMotion>
    </MotionErrorBoundary>
  );
};

export default HeroContent;
